import { Link } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import useAuthStore from '@/store/authStore';
import useUIStore from '@/store/uiStore';
import { ROUTES } from '@/utils/constants';
import SidebarNav from './SidebarNav';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import BrandLogo from '@/components/common/BrandLogo';

const SIDEBAR_WIDTH = 285;
const SIDEBAR_COLLAPSED_WIDTH = 72;

interface SidebarProps {
  isMobile?: boolean;
  onClose?: () => void;
}

const Sidebar = ({ isMobile = false, onClose }: SidebarProps) => {
  const { user, logout } = useAuthStore();
  const { isSidebarCollapsed } = useUIStore();
  const collapsed = isMobile ? false : isSidebarCollapsed;

  const handleLogout = () => {
    logout();
    if (onClose) onClose();
  };

  const initials =
    [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('').toUpperCase() || 'A';

  return (
    <div
      className="flex flex-col h-full bg-sidebar text-sidebar-foreground transition-all duration-300 shadow-navy-glow"
      style={{ width: isMobile ? 280 : collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH }}
    >
      {/* ── Logo ── */}
      <div
        className={cn(
          'shrink-0 flex items-center border-b border-sidebar-border',
          'h-16 px-4 transition-all duration-300',
          collapsed && !isMobile && 'justify-center px-0',
        )}
      >
        <Link to={ROUTES.MAIN} className="flex items-center gap-3 group">
          <BrandLogo collapsed={true} className="rounded-xl h-[50px] w-[50px]" />
          {(!collapsed || isMobile) && (
            <div className="flex flex-col">
              <span className="font-display uppercase font-bold text-sidebar-foreground text-[17px] leading-tight tracking-tight">
                Kipi Admin
              </span>
              <span className="text-[10px] text-sidebar-muted font-bold tracking-widest uppercase">
                Panel
              </span>
            </div>
          )}
        </Link>

        {/* Mobile close button — glass rounded-square that matches the black/gold
            sidebar identity and picks up the gold accent on interaction. */}
        {isMobile && onClose && (
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="ml-auto flex items-center justify-center p-2 rounded-lg text-sidebar-muted hover:text-primary transition-all active:scale-95 cursor-pointer shrink-0 group/close"
          >
            <div className="relative w-5 h-5 flex items-center justify-center">
              <span className="absolute h-0.5 w-5 bg-sidebar-foreground rounded-full rotate-45 transition-all duration-300 group-hover/close:rotate-[135deg]" />
              <span className="absolute h-0.5 w-5 bg-gradient-to-r from-[#FFD941] via-[#FEC448] to-[#CC8400] rounded-full -rotate-45 transition-all duration-300 group-hover/close:rotate-[45deg]" />
            </div>
          </button>
        )}
      </div>

      {/* ── Navigation ── */}
      <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
        <SidebarNav collapsed={collapsed && !isMobile} onNavigate={onClose} />
      </div>

      {/* ── Footer ── */}
      <div className="shrink-0 border-t border-sidebar-border">
        {/* Profile link */}
        <Link
          to={ROUTES.PROFILE}
          onClick={onClose}
          className={cn(
            'flex items-center gap-3 m-2 px-2 py-2 rounded-xl cursor-pointer transition-all duration-200',
            'hover:bg-sidebar-accent',
            collapsed && 'justify-center',
          )}
        >
          <Avatar className="w-8 h-8 shrink-0 ring-2 ring-sidebar-primary/50">
            <AvatarFallback className="text-xs font-bold bg-sidebar-primary text-sidebar-primary-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate text-sidebar-foreground">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-[11px] truncate text-sidebar-muted font-medium">{user?.email}</p>
            </div>
          )}
        </Link>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={cn(
            'flex items-center gap-3 mx-2 mb-2 px-3 h-10 rounded-xl cursor-pointer transition-all duration-200',
            'text-red-400 hover:bg-destructive/15 hover:text-red-300 text-[13px] font-medium',
            collapsed ? 'justify-center w-10' : 'w-[calc(100%-16px)]',
          )}
          title={collapsed ? 'Logout' : undefined}
        >
          <LogOut size={17} className="shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
